<!-- 商家端 - 订单管理列表 -->
<template>
  <div>
    <!-- 搜索栏：与商品管理/平台订单一致，单独 ContentWrap，避免 ElCard overflow 裁切 -->
    <ContentWrap :body-style="{ padding: '16px 16px 2px', overflow: 'visible' }">
      <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="88px">
        <el-form-item label="订单号" prop="no">
          <el-input
            v-model="queryParams.no"
            class="!w-200px"
            clearable
            placeholder="请输入订单号"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="订单状态" prop="status">
          <el-select v-model="queryParams.status" class="!w-180px" clearable placeholder="全部">
            <el-option label="待付款" :value="0" />
            <el-option label="待发货/待核销" :value="10" />
            <el-option label="已发货" :value="20" />
            <el-option v-if="forceLiveOnly" label="已送到" :value="21" />
            <el-option label="已完成" :value="30" />
            <el-option label="已取消" :value="40" />
          </el-select>
        </el-form-item>
        <el-form-item label="配送方式" prop="deliveryType">
          <el-select
            v-model="queryParams.deliveryType"
            class="!w-160px"
            clearable
            placeholder="全部"
          >
            <el-option
              :label="DeliveryTypeEnum.EXPRESS.name"
              :value="DeliveryTypeEnum.EXPRESS.type"
            />
            <el-option
              :label="DeliveryTypeEnum.PICK_UP.name"
              :value="DeliveryTypeEnum.PICK_UP.type"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="创建时间" prop="createTime">
          <el-date-picker
            v-model="queryParams.createTime"
            :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
            class="!w-280px"
            end-placeholder="结束日期"
            start-placeholder="开始日期"
            type="daterange"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
        <el-form-item label="直播间ID" prop="liveRoomId">
          <el-input
            v-model="queryParams.liveRoomId"
            class="!w-160px"
            clearable
            placeholder="直播间ID"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item v-if="!forceLiveOnly" label="带货单" prop="liveOnly">
          <el-switch
            v-model="queryParams.liveOnly"
            inline-prompt
            active-text="仅直播"
            inactive-text="全部"
          />
        </el-form-item>
        <el-form-item v-else label="带货单">
          <el-tag type="warning">仅直播</el-tag>
        </el-form-item>
        <el-form-item>
          <div class="flex flex-wrap items-center gap-8px">
            <el-button @click="handleQuery">
              <Icon class="mr-5px" icon="ep:search" />搜索
            </el-button>
            <el-button @click="resetQuery">
              <Icon class="mr-5px" icon="ep:refresh" />重置
            </el-button>
            <el-button type="success" plain :loading="exportLoading" @click="handleExport">
              <Icon class="mr-5px" icon="ep:download" />导出
            </el-button>
            <el-button
              type="warning"
              plain
              :loading="exportDeliveryLoading"
              @click="handleExportDelivery"
            >
              <Icon class="mr-5px" icon="ep:download" />导出待发货
            </el-button>
            <el-button type="warning" plain @click="openImportDelivery">
              <Icon class="mr-5px" icon="ep:upload" />导入发货
            </el-button>
            <el-button type="primary" plain @click="openPickUpByCode">
              <Icon class="mr-5px" icon="ep:ticket" />核销码核销
            </el-button>
            <el-button type="success" plain @click="goVerifyPage">
              <Icon class="mr-5px" icon="ep:full-screen" />扫码核销
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <!-- 列表 -->
    <ContentWrap :body-style="{ padding: '16px', overflow: 'visible' }">
      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column label="订单号" min-width="180" prop="no" show-overflow-tooltip />
        <el-table-column label="商品信息" min-width="260">
          <template #default="{ row }">
            <div
              v-for="item in (row.items || []).slice(0, 2)"
              :key="item.id"
              class="mb-4px flex min-w-0 items-center"
            >
              <el-image
                :src="item.picUrl"
                class="mr-8px h-40px w-40px shrink-0 rounded-4px"
                fit="cover"
              />
              <span class="min-w-0 flex-1 text-13px line-clamp-1">{{ item.spuName }}</span>
              <span class="ml-8px shrink-0 text-13px text-gray-400">x{{ item.count }}</span>
            </div>
            <span v-if="(row.items || []).length > 2" class="text-12px text-gray-400">
              等{{ row.items.length }}件商品
            </span>
          </template>
        </el-table-column>
        <el-table-column label="实付金额" width="110">
          <template #default="{ row }">{{ fenToYuan(row.payPrice) }} 元</template>
        </el-table-column>
        <el-table-column label="收货人" width="100" prop="receiverName" show-overflow-tooltip />
        <el-table-column label="配送" width="90">
          <template #default="{ row }">
            {{ deliveryTypeLabel(row.deliveryType) }}
          </template>
        </el-table-column>
        <el-table-column label="订单状态" width="110">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row)">{{ statusLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="直播间ID" width="110">
          <template #default="{ row }">
            {{ row.liveRoomId != null && row.liveRoomId !== '' ? row.liveRoomId : '—' }}
          </template>
        </el-table-column>
        <el-table-column
          label="创建时间"
          width="170"
          prop="createTime"
          :formatter="dateFormatter"
        />
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="{ row }">
            <div class="flex flex-wrap items-center gap-x-4px">
              <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
              <el-button
                v-if="forceLiveOnly && row.status === 20 && row.logisticsId > 0"
                link
                type="primary"
                @click="syncLogistics(row.id)"
              >
                同步物流
              </el-button>
              <el-button v-if="canDeliver(row)" link type="success" @click="openDelivery(row)">
                发货
              </el-button>
              <el-tooltip
                v-if="isExpressUndelivered(row) && row.deliveryBySupplier"
                content="外发订单请由供应商发货"
                placement="top"
              >
                <el-button link type="info" disabled>供应商发货</el-button>
              </el-tooltip>
              <el-button v-if="canPickUp(row)" link type="success" @click="handlePickUp(row)">
                核销
              </el-button>
              <el-button link type="warning" @click="openRemark(row)">备注</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </ContentWrap>

    <!-- 发货弹窗：对齐平台 OrderDeliveryForm（选物流公司 logisticsId / 无需发货） -->
    <el-dialog
      v-model="deliveryDialogVisible"
      title="订单发货"
      width="500px"
      @opened="loadExpressList"
    >
      <el-form ref="deliveryFormRef" :model="deliveryForm" label-width="80px">
        <el-form-item label="发货方式">
          <el-radio-group v-model="expressType">
            <el-radio border value="express">快递物流</el-radio>
            <el-radio border value="none">无需发货</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="expressType === 'express'">
          <el-form-item label="物流公司" required>
            <el-select
              v-model="deliveryForm.logisticsId"
              class="!w-full"
              filterable
              clearable
              placeholder="请选择物流公司"
            >
              <el-option
                v-for="item in deliveryExpressList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="物流单号" required>
            <el-input v-model="deliveryForm.logisticsNo" placeholder="请输入物流单号" />
          </el-form-item>
        </template>
      </el-form>
      <template #footer>
        <el-button @click="deliveryDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="deliverySubmitting" @click="submitDelivery">
          确认发货
        </el-button>
      </template>
    </el-dialog>

    <!-- 核销码核销 -->
    <el-dialog v-model="pickUpCodeVisible" title="核销码核销" width="480px">
      <el-form label-width="80px" @submit.prevent>
        <el-form-item label="核销码" required>
          <el-input
            v-model="pickUpVerifyCode"
            clearable
            placeholder="请输入顾客出示的自提核销码"
            @keyup.enter="previewPickUpByCode"
          />
        </el-form-item>
        <el-form-item v-if="pickUpPreview" label="订单">
          <div class="text-13px">
            <div>单号：{{ pickUpPreview.no }}</div>
            <div>实付：{{ fenToYuan(pickUpPreview.payPrice) }} 元</div>
            <div>状态：{{ statusLabel(pickUpPreview) }}</div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="pickUpCodeVisible = false">取消</el-button>
        <el-button :loading="pickUpPreviewLoading" @click="previewPickUpByCode">查询</el-button>
        <el-button
          type="primary"
          :disabled="!pickUpPreview"
          :loading="pickUpSubmitting"
          @click="submitPickUpByCode"
        >
          确认核销
        </el-button>
      </template>
    </el-dialog>

    <!-- 备注弹窗 -->
    <el-dialog v-model="remarkDialogVisible" title="订单备注" width="500px">
      <el-input
        v-model="remarkForm.remark"
        :rows="4"
        type="textarea"
        placeholder="请输入备注信息"
      />
      <template #footer>
        <el-button @click="remarkDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRemark">保存</el-button>
      </template>
    </el-dialog>

    <!-- 订单详情：留在 /store 壳内，走 merchant-api，禁止跳 A 端 /trade/order/detail -->
    <el-drawer v-model="detailVisible" :title="detailTitle" size="640px" destroy-on-close>
      <div v-loading="detailLoading">
        <template v-if="detail">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="订单号">{{ detail.no }}</el-descriptions-item>
            <el-descriptions-item label="状态">{{ statusLabel(detail) }}</el-descriptions-item>
            <el-descriptions-item label="配送">{{
              deliveryTypeLabel(detail.deliveryType)
            }}</el-descriptions-item>
            <el-descriptions-item label="实付"
              >{{ fenToYuan(detail.payPrice) }} 元</el-descriptions-item
            >
            <el-descriptions-item label="收货人">{{
              detail.receiverName || '—'
            }}</el-descriptions-item>
            <el-descriptions-item label="手机">{{
              detail.receiverMobile || '—'
            }}</el-descriptions-item>
            <el-descriptions-item label="地址" :span="2">
              {{ detail.receiverAreaName || '' }} {{ detail.receiverDetailAddress || '—' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.logisticsNo" label="物流单号" :span="2">
              {{ detail.logisticsNo }}
            </el-descriptions-item>
            <el-descriptions-item label="创建时间">{{
              formatDate(detail.createTime)
            }}</el-descriptions-item>
            <el-descriptions-item label="商家备注">{{ detail.remark || '—' }}</el-descriptions-item>
            <!-- 优惠构成：与 A 端详情同数据源（TradeOrderDetailRespVO），解释实付与单价差 -->
            <el-descriptions-item v-if="Number(detail.totalPrice) > 0" label="商品总额">
              {{ fenToYuan(detail.totalPrice) }} 元
            </el-descriptions-item>
            <el-descriptions-item v-if="Number(detail.discountPrice) > 0" label="活动优惠">
              -{{ fenToYuan(detail.discountPrice) }} 元
            </el-descriptions-item>
            <el-descriptions-item v-if="Number(detail.vipPrice) > 0" label="会员优惠">
              -{{ fenToYuan(detail.vipPrice) }} 元
            </el-descriptions-item>
            <el-descriptions-item v-if="Number(detail.couponPrice) > 0" label="优惠券">
              -{{ fenToYuan(detail.couponPrice) }} 元
            </el-descriptions-item>
            <el-descriptions-item v-if="Number(detail.pointPrice) > 0" label="积分抵扣">
              -{{ fenToYuan(detail.pointPrice) }} 元
              <span v-if="Number(detail.usePoint) > 0" class="ml-8px text-gray-500">
                （消耗 {{ detail.usePoint }} 积分）
              </span>
            </el-descriptions-item>
            <el-descriptions-item
              v-else-if="Number(detail.usePoint) > 0"
              label="消耗积分"
            >
              {{ detail.usePoint }}
            </el-descriptions-item>
          </el-descriptions>
          <div class="mt-12px flex flex-wrap gap-8px">
            <el-button
              v-if="detail.status === TradeOrderStatusEnum.UNPAID.status"
              type="primary"
              @click="openUpdatePrice"
            >
              调整价格
            </el-button>
            <el-button
              v-if="
                detail.status === TradeOrderStatusEnum.UNDELIVERED.status &&
                detail.deliveryType === DeliveryTypeEnum.EXPRESS.type
              "
              type="primary"
              @click="openUpdateAddress"
            >
              修改地址
            </el-button>
            <el-button type="warning" @click="openRemark(detail)">备注</el-button>
            <el-button v-if="showInvoiceAction" type="primary" plain @click="goInvoice">
              {{ detailInvoice?.status === INVOICE_STATUS.PENDING ? '去开票' : '查看发票' }}
            </el-button>
          </div>
          <el-table :data="detail.items || []" class="mt-16px" stripe>
            <el-table-column label="商品" min-width="200">
              <template #default="{ row }">
                <div class="flex min-w-0 items-center">
                  <el-image
                    v-if="row.picUrl"
                    :src="row.picUrl"
                    class="mr-8px h-40px w-40px shrink-0 rounded-4px"
                    fit="cover"
                  />
                  <div class="min-w-0">
                    <div class="text-13px line-clamp-2">{{ row.spuName }}</div>
                    <div v-if="row.properties?.length" class="text-12px text-gray-400">
                      {{
                        row.properties.map((p: any) => `${p.propertyName}:${p.valueName}`).join(' ')
                      }}
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="单价" width="90">
              <template #default="{ row }">{{ fenToYuan(row.price) }}</template>
            </el-table-column>
            <el-table-column label="数量" prop="count" width="70" />
            <el-table-column label="小计" width="90">
              <template #default="{ row }">{{ fenToYuan(row.payPrice) }}</template>
            </el-table-column>
          </el-table>
          <div v-if="detail.logs?.length" class="mt-16px">
            <div class="mb-8px text-14px font-medium">操作日志</div>
            <el-timeline>
              <el-timeline-item
                v-for="(log, idx) in detail.logs"
                :key="idx"
                :timestamp="formatDate(log.createTime)"
              >
                {{ log.content }}
              </el-timeline-item>
            </el-timeline>
          </div>
        </template>
      </div>
    </el-drawer>

    <OrderUpdatePriceForm ref="updatePriceFormRef" @success="onDetailMutated" />
    <OrderUpdateAddressForm ref="updateAddressFormRef" @success="onDetailMutated" />
    <OrderDeliveryImportForm
      ref="importDeliveryFormRef"
      :import-api="MerchantOrderApi.importDelivery"
      @success="getList"
    />
  </div>
</template>

<script setup lang="ts">
import * as MerchantOrderApi from '@/api/merchant/order'
import * as InvoiceApi from '@/api/merchant/invoice'
import { INVOICE_STATUS } from '@/api/merchant/invoice'
import { getMerchantSession } from '@/views/merchant/registry/merchant-session'
import { isPlatformMallBizMode } from '@/views/merchant/registry/mall-biz-mode'
import * as MerchantExpressApi from '@/api/merchant/delivery/express'
import type { DeliveryExpressSimpleVO } from '@/api/merchant/delivery/express'
import OrderUpdatePriceForm from './form/OrderUpdatePriceForm.vue'
import OrderUpdateAddressForm from './form/OrderUpdateAddressForm.vue'
import OrderDeliveryImportForm from '@/views/mall/trade/order/form/OrderDeliveryImportForm.vue'
import { fenToYuan } from '@/utils'
import { normalizePickUpVerifyCode } from '@/utils/pickUpVerifyCode'
import { dateFormatter, formatDate } from '@/utils/formatTime'
import { DeliveryTypeEnum, TradeOrderStatusEnum } from '@/utils/constants'
import download from '@/utils/download'

defineOptions({ name: 'MerchantOrderList' })

const props = withDefaults(
  defineProps<{
    /** 直播带货订单入口：强制仅查直播单 */
    forceLiveOnly?: boolean
  }>(),
  { forceLiveOnly: false }
)

const message = useMessage()
const router = useRouter()
const detailInvoice = ref<any>(null)
const platformInvoice = computed(() => isPlatformMallBizMode(getMerchantSession()?.mallBizMode))
const showInvoiceAction = computed(() => platformInvoice.value && !!detailInvoice.value)

const loading = ref(false)
const exportLoading = ref(false)
const exportDeliveryLoading = ref(false)
const importDeliveryFormRef = ref<InstanceType<typeof OrderDeliveryImportForm>>()
const list = ref<any[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  no: '',
  status: undefined as number | undefined,
  deliveryType: undefined as number | undefined,
  createTime: [] as string[],
  liveRoomId: '' as string,
  liveOnly: props.forceLiveOnly
})

watch(
  () => props.forceLiveOnly,
  (v) => {
    if (v) queryParams.liveOnly = true
  }
)

const deliveryDialogVisible = ref(false)
const deliverySubmitting = ref(false)
const currentOrderId = ref(0)
/** express=快递物流；none=无需发货（logisticsId=0） */
const expressType = ref<'express' | 'none'>('express')
const deliveryForm = reactive<{ logisticsId: number | undefined; logisticsNo: string }>({
  logisticsId: undefined,
  logisticsNo: ''
})
const deliveryExpressList = ref<DeliveryExpressSimpleVO[]>([])

const remarkDialogVisible = ref(false)
const remarkForm = reactive({ id: 0, remark: '' })

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<any>(null)
const detailTitle = computed(() =>
  detail.value?.no ? `订单详情 · ${detail.value.no}` : '订单详情'
)
const updatePriceFormRef = ref<InstanceType<typeof OrderUpdatePriceForm>>()
const updateAddressFormRef = ref<InstanceType<typeof OrderUpdateAddressForm>>()

const pickUpCodeVisible = ref(false)
const pickUpVerifyCode = ref('')
const pickUpPreview = ref<any>(null)
const pickUpPreviewLoading = ref(false)
const pickUpSubmitting = ref(false)

const isPickUp = (row: any) => row?.deliveryType === DeliveryTypeEnum.PICK_UP.type
const isExpressUndelivered = (row: any) =>
  row?.status === 10 && row?.deliveryType === DeliveryTypeEnum.EXPRESS.type
const canDeliver = (row: any) => isExpressUndelivered(row) && !row.deliveryBySupplier
const canPickUp = (row: any) => row?.status === 10 && isPickUp(row)

const deliveryTypeLabel = (type: number) => {
  if (type === DeliveryTypeEnum.EXPRESS.type) return DeliveryTypeEnum.EXPRESS.name
  if (type === DeliveryTypeEnum.PICK_UP.type) return DeliveryTypeEnum.PICK_UP.name
  return '—'
}

const statusLabel = (row: any) => {
  if (row?.status === 10 && isPickUp(row)) return '待核销'
  const map: Record<number, string> = {
    0: '待付款',
    10: '待发货',
    20: row?.logisticsSignTime ? '已送到' : '已发货',
    30: '已完成',
    40: '已取消'
  }
  return map[row?.status] || '未知'
}

const statusTagType = (row: any) => {
  if (row?.status === 10 && isPickUp(row)) return 'warning'
  const map: Record<number, 'warning' | 'primary' | 'success' | 'info' | undefined> = {
    0: 'warning',
    10: 'primary',
    20: 'success',
    30: undefined,
    40: 'info'
  }
  return map[row?.status] || 'info'
}

const buildPageParams = () => {
  const params: Record<string, any> = {
    pageNo: queryParams.pageNo,
    pageSize: queryParams.pageSize,
    no: queryParams.no || undefined,
    status: queryParams.status === 21 ? 20 : queryParams.status,
    logisticsSigned:
      props.forceLiveOnly && queryParams.status === 21
        ? true
        : props.forceLiveOnly && queryParams.status === 20
          ? false
          : undefined,
    deliveryType: queryParams.deliveryType,
    liveRoomId: queryParams.liveRoomId || undefined,
    liveOnly: queryParams.liveOnly || undefined
  }
  if (queryParams.createTime?.length === 2) {
    params.createTimeStart = queryParams.createTime[0]
    params.createTimeEnd = queryParams.createTime[1]
  }
  return params
}

const syncLogistics = async (id: number) => {
  await MerchantOrderApi.syncOrderLogistics(id)
  message.success('物流状态已同步')
  await getList()
}

const getList = async () => {
  loading.value = true
  try {
    const data = await MerchantOrderApi.getOrderPage(buildPageParams())
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}
const resetQuery = () => {
  queryParams.no = ''
  queryParams.status = undefined
  queryParams.deliveryType = undefined
  queryParams.createTime = []
  queryParams.liveRoomId = ''
  queryParams.liveOnly = props.forceLiveOnly
  handleQuery()
}

const handleExport = async () => {
  try {
    await message.exportConfirm()
  } catch {
    return
  }
  exportLoading.value = true
  try {
    const data = await MerchantOrderApi.exportOrder(buildPageParams())
    download.excel(data as Blob, '订单列表.xls')
  } finally {
    exportLoading.value = false
  }
}

const handleExportDelivery = async () => {
  try {
    await message.confirm('将导出本店快递待发货订单（已排除须供应商发货的外发单），是否继续？')
  } catch {
    return
  }
  exportDeliveryLoading.value = true
  try {
    const data = await MerchantOrderApi.exportDelivery(buildPageParams())
    download.excel(data as Blob, '待发货发货清单.xlsx')
  } finally {
    exportDeliveryLoading.value = false
  }
}

const openImportDelivery = () => {
  importDeliveryFormRef.value?.open()
}

const openDetail = async (id: number) => {
  detailVisible.value = true
  detailLoading.value = true
  detail.value = null
  detailInvoice.value = null
  try {
    detail.value = await MerchantOrderApi.getOrder(id)
    if (platformInvoice.value) {
      try {
        detailInvoice.value = await InvoiceApi.getInvoiceByOrder(id)
      } catch {
        detailInvoice.value = null
      }
    }
  } catch {
    message.error('加载订单详情失败')
    detailVisible.value = false
  } finally {
    detailLoading.value = false
  }
}

const goInvoice = () => {
  if (!detail.value?.id) return
  router.push({ path: '/store/finance/invoice', query: { orderId: String(detail.value.id) } })
}

const openUpdatePrice = () => {
  if (!detail.value) return
  updatePriceFormRef.value?.open(detail.value)
}
const openUpdateAddress = () => {
  if (!detail.value) return
  updateAddressFormRef.value?.open(detail.value)
}
const onDetailMutated = async () => {
  if (detail.value?.id) {
    await openDetail(detail.value.id)
  }
  getList()
}

const loadExpressList = async () => {
  if (deliveryExpressList.value.length) return
  try {
    deliveryExpressList.value = (await MerchantExpressApi.getSimpleDeliveryExpressList()) || []
  } catch {
    deliveryExpressList.value = []
  }
}

const openDelivery = (row: any) => {
  currentOrderId.value = row.id
  expressType.value = 'express'
  deliveryForm.logisticsId = undefined
  deliveryForm.logisticsNo = ''
  deliveryDialogVisible.value = true
}

const submitDelivery = async () => {
  let logisticsId: number
  let logisticsNo: string
  if (expressType.value === 'none') {
    logisticsId = 0
    logisticsNo = ''
  } else {
    if (deliveryForm.logisticsId == null) {
      message.warning('请选择物流公司')
      return
    }
    if (!deliveryForm.logisticsNo?.trim()) {
      message.warning('请输入物流单号')
      return
    }
    logisticsId = deliveryForm.logisticsId
    logisticsNo = deliveryForm.logisticsNo.trim()
  }
  deliverySubmitting.value = true
  try {
    await MerchantOrderApi.deliveryOrder({
      id: currentOrderId.value,
      logisticsId,
      logisticsNo
    })
    message.success('发货成功')
    deliveryDialogVisible.value = false
    getList()
  } finally {
    deliverySubmitting.value = false
  }
}

const handlePickUp = async (row: any) => {
  try {
    await message.confirm(`确认核销订单 ${row.no} 吗？核销后订单将完成。`)
  } catch {
    return
  }
  await MerchantOrderApi.pickUpOrder(row.id)
  message.success('核销成功')
  getList()
}

const openPickUpByCode = () => {
  pickUpVerifyCode.value = ''
  pickUpPreview.value = null
  pickUpCodeVisible.value = true
}

const goVerifyPage = () => {
  router.push('/store/order/verify')
}

const previewPickUpByCode = async () => {
  const code = normalizePickUpVerifyCode(pickUpVerifyCode.value)
  if (!code) {
    message.warning('请输入核销码')
    return
  }
  pickUpVerifyCode.value = code
  pickUpPreviewLoading.value = true
  pickUpPreview.value = null
  try {
    const data = await MerchantOrderApi.getOrderByPickUpVerifyCode(code)
    if (data?.deliveryType !== DeliveryTypeEnum.PICK_UP.type) {
      message.error('该订单不是自提订单')
      return
    }
    if (data?.status !== 10) {
      message.error('订单不是待核销状态')
      return
    }
    pickUpPreview.value = data
  } finally {
    pickUpPreviewLoading.value = false
  }
}

const submitPickUpByCode = async () => {
  const code = normalizePickUpVerifyCode(pickUpVerifyCode.value)
  if (!code || !pickUpPreview.value) return
  pickUpSubmitting.value = true
  try {
    await MerchantOrderApi.pickUpOrderByVerifyCode(code)
    message.success('核销成功')
    pickUpCodeVisible.value = false
    getList()
  } finally {
    pickUpSubmitting.value = false
  }
}

const openRemark = (row: any) => {
  remarkForm.id = row.id
  remarkForm.remark = row.remark || ''
  remarkDialogVisible.value = true
}
const submitRemark = async () => {
  await MerchantOrderApi.updateRemark({ id: remarkForm.id, remark: remarkForm.remark })
  message.success('保存成功')
  remarkDialogVisible.value = false
  getList()
  if (detailVisible.value && detail.value?.id === remarkForm.id) {
    await openDetail(remarkForm.id)
  }
}

onMounted(() => {
  getList()
})
// 父级 KeepAlive：再次切回「订单管理」时刷新
onActivated(() => {
  getList()
})
</script>
